import { z } from 'zod';
import { sectionHasValue } from '../util';
import { registry } from '../registry';
export const baseTemperature = z
    .object({
    notChecked: z.boolean().optional().register(registry, { label: 'WNL' }),
    temp: z.string().optional().register(registry, { label: 'Temperature (F)' }),
    via: z
        .string()
        .optional()
        .register(registry, {
        label: 'Via',
        options: [
            { value: 'oral', label: 'Oral' },
            { value: 'axillary', label: 'Axillary' },
            { value: 'rectal', label: 'Rectal' },
            { value: 'temporal', label: 'Temporal' },
            { value: 'tympanic', label: 'Tympanic' },
            { value: 'non-contact', label: 'Non-contact' },
        ],
    }),
})
    .register(registry, { label: 'Temperature' });
export const baseTemperatureRefined = baseTemperature.refine((data) => data.notChecked || data.temp?.trim() || data.via?.trim(), {
    message: 'At least one answer is required',
    path: [],
});
export const baseO2 = z
    .object({
    notChecked: z.boolean().optional().register(registry, { label: 'Not Captured' }),
    o2Sat: z.string().optional().register(registry, { label: 'O2 Saturation %' }),
    flow: z
        .string()
        .optional()
        .register(registry, {
        label: 'Flow',
        options: [
            { value: 'roomAir', label: 'On Room Air' },
            { value: 'flowRate', label: 'Supplemental O2' },
        ],
    }),
    lpm: z.string().optional().register(registry, { label: 'at LPM' }),
    via: z
        .string()
        .optional()
        .register(registry, {
        label: 'via',
        options: [
            { value: 'nc', label: 'NC' },
            { value: 'mask', label: 'Mask' },
            { value: 'other', label: 'Other' },
        ],
    }),
    viaOther: z.string().optional().register(registry, { label: 'Other' }),
})
    .register(registry, { label: 'Oxygen Saturation' });
export const baseO2Refined = baseO2.refine((data) => sectionHasValue(data), {
    message: 'At least one answer is required',
    path: [],
});
export const baseRespiration = z
    .object({
    notChecked: z.boolean().optional().register(registry, { label: 'Not Captured' }),
    resp: z.string().optional().register(registry, { label: 'Respiration (/min)' }),
    respReg: z
        .string()
        .optional()
        .register(registry, {
        label: 'Rhythm',
        options: [
            { value: 'regular', label: 'Regular' },
            { value: 'irregular', label: 'Irregular' },
        ],
    }),
    effort: z
        .string()
        .optional()
        .register(registry, {
        label: 'Effort',
        options: [
            { value: 'unlabored', label: 'Unlabored' },
            { value: 'labored', label: 'Labored' },
            { value: 'accessoryMuscleUse', label: 'Accessory muscle use' },
        ],
    }),
})
    .register(registry, { label: 'Respiration Rate' });
export const baseRespirationRefined = baseRespiration.refine((data) => sectionHasValue(data), {
    message: 'At least one answer is required',
    path: [],
});
export const basePulse = z
    .object({
    notChecked: z.boolean().optional().register(registry, { label: 'Not Captured' }),
    pulse: z.string().optional().register(registry, { label: 'Pulse/Heart Rate (/min)' }),
    pulseApical: z
        .string()
        .optional()
        .register(registry, {
        label: 'Site',
        options: [
            { value: 'apical', label: 'Apical' },
            { value: 'radial', label: 'Radial' },
        ],
    }),
    pulseReg: z
        .string()
        .optional()
        .register(registry, {
        label: 'Rhythm',
        options: [
            { value: 'regular', label: 'Regular' },
            { value: 'regularly irregular', label: 'Regularly irregular' },
            { value: 'irregularly irregular', label: 'Irregularly irregular' },
        ],
    }),
})
    .register(registry, { label: 'Pulse/Heart Rate' });
export const basePulseRefined = basePulse.refine((data) => data.notChecked || data.pulse?.trim() || data.pulseApical?.trim() || data.pulseReg?.trim(), {
    message: 'At least one answer is required',
    path: [],
});
export const baseBloodSugar = z
    .object({
    notChecked: z.boolean().optional().register(registry, { label: 'Not Captured' }),
    fasting: z.string().optional().nullable().register(registry, { label: 'Fasting' }),
    random: z.string().optional().nullable().register(registry, { label: 'Random' }),
    preMeal: z.string().optional().nullable().register(registry, { label: 'Pre-meal' }),
    postMeal: z.string().optional().nullable().register(registry, { label: 'Post-meal' }),
    bedtime: z.string().optional().nullable().register(registry, { label: 'Bedtime' }),
    timeSinceLastMeal: z.string().optional().nullable().register(registry, { label: 'Time since last meal' }),
})
    .register(registry, { label: 'Blood Sugar (mg/dl)' });
export const baseBloodSugarRefined = baseBloodSugar.refine((data) => sectionHasValue(data), {
    message: 'At least one answer is required',
    path: [],
});
export const baseBloodPressureEntry = z.object({
    systolic: z.string().optional().register(registry, { label: 'Systolic' }),
    diastolic: z.string().optional().register(registry, { label: 'Diastolic' }),
    position: z
        .string()
        .optional()
        .register(registry, {
        label: 'Position',
        options: [
            { value: 'sitting', label: 'Sitting' },
            { value: 'sitting-orthostatic', label: 'Sitting (after 1-3 min)' },
            { value: 'standing', label: 'Standing' },
            { value: 'standing-orthostatic', label: 'Standing (after 1-3 min)' },
            { value: 'laying', label: 'Laying' },
            { value: 'lying-orthostatic', label: 'Lying (after 5 min supine)' },
        ],
    }),
    arm: z
        .string()
        .optional()
        .register(registry, {
        label: 'Arm',
        options: [
            { value: 'left', label: 'Left' },
            { value: 'right', label: 'Right' },
        ],
    }),
    option: z
        .string()
        .optional()
        .register(registry, {
        label: 'Orthostatic Symptoms',
        options: [
            { value: 'none', label: 'None' },
            { value: 'dizziness', label: 'Dizziness' },
            { value: 'lightheadedness', label: 'Lightheadedness' },
            { value: 'syncope', label: 'Syncope' },
            { value: 'palpitations', label: 'Palpitations' },
            { value: 'unsteady', label: 'Unsteady' },
        ],
    }),
});
export const baseBloodPressure = z
    .object({
    notChecked: z.boolean().optional().register(registry, { label: 'Not Captured' }),
    bloodPressures: z.array(baseBloodPressureEntry).optional(),
})
    .register(registry, { label: 'Blood Pressure' });
